import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, BarChart3, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

export const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-6 bg-white/80 backdrop-blur-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="relative">
              <TrendingUp className="h-7 w-7 text-[#C9A64A]" />
              <BarChart3 className="h-4 w-4 text-[#C9A64A] absolute -bottom-1 -right-1" />
            </div>
            <span className="text-xl font-bold text-[#1A1A1C] tracking-tight">Virtual<span className="text-[#C9A64A]">CFO</span></span>
          </Link>
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 bg-[#C9A64A]/10 rounded-full">
              <Shield className="h-5 w-5 text-[#C9A64A]" />
              <span className="text-[#C9A64A] text-sm font-bold tracking-widest uppercase">Privacy & Security</span>
            </div>
            <h1 className="text-5xl font-bold text-[#1A1A1C] mb-6">Privacy Policy</h1>
            <p className="text-xl text-[#5A6270]">Last updated: January 2025</p>
          </div>

          <div className="prose prose-lg max-w-none space-y-8">
            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Introduction</h2>
              <p className="text-[#5A6270] leading-relaxed">
                Virtual CFO ("we," "us," or "our") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website virtualcfo.finance and use our services.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-[#1A1A1C] mt-6 mb-3">Personal Information</h3>
              <p className="text-[#5A6270] leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc list-inside text-[#5A6270] space-y-2 ml-4">
                <li>Submit a contact form</li>
                <li>Subscribe to our newsletter</li>
                <li>Use our calculators or tools</li>
                <li>Engage our Virtual CFO services</li>
              </ul>
              <p className="text-[#5A6270] leading-relaxed mt-4">
                This information may include: name, email address, company name, phone number, and any other information you choose to provide.
              </p>

              <h3 className="text-xl font-semibold text-[#1A1A1C] mt-6 mb-3">Automatically Collected Information</h3>
              <p className="text-[#5A6270] leading-relaxed">
                When you visit our website, we may automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies installed on your device. We may also collect information about your browsing actions and patterns.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">How We Use Your Information</h2>
              <p className="text-[#5A6270] leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-[#5A6270] space-y-2 ml-4">
                <li>Respond to your inquiries and provide customer support</li>
                <li>Send you information about our services</li>
                <li>Improve our website and services</li>
                <li>Analyze usage trends and preferences</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Information Sharing and Disclosure</h2>
              <p className="text-[#5A6270] leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-[#5A6270] space-y-2 ml-4">
                <li><strong>Service Providers:</strong> We may share information with third-party service providers who perform services on our behalf (e.g., email delivery, analytics)</li>
                <li><strong>Legal Requirements:</strong> We may disclose information if required by law or in response to valid legal requests</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>With Your Consent:</strong> We may share information with your explicit consent</li>
              </ul>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Data Security</h2>
              <p className="text-[#5A6270] leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Cookies and Tracking Technologies</h2>
              <p className="text-[#5A6270] leading-relaxed">
                We use cookies and similar tracking technologies to track activity on our website and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Your Privacy Rights</h2>
              <p className="text-[#5A6270] leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside text-[#5A6270] space-y-2 ml-4">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to or restrict processing of your information</li>
                <li>Request data portability</li>
                <li>Withdraw consent where processing is based on consent</li>
              </ul>
              <p className="text-[#5A6270] leading-relaxed mt-4">
                To exercise these rights, please contact us using the information below.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Data Retention</h2>
              <p className="text-[#5A6270] leading-relaxed">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Children's Privacy</h2>
              <p className="text-[#5A6270] leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us immediately.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Third-Party Links</h2>
              <p className="text-[#5A6270] leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these websites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Changes to This Privacy Policy</h2>
              <p className="text-[#5A6270] leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl bg-gradient-to-br from-[#C9A64A]/5 to-transparent border-2 border-[#C9A64A]/20">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Contact Us</h2>
              <p className="text-[#5A6270] leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="space-y-2">
                <p className="text-[#1A1A1C] font-medium">
                  Email: <a href="mailto:twopearlsprivatelimited@gmail.com" className="text-[#C9A64A] hover:underline">twopearlsprivatelimited@gmail.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
